import jsPDF from "jspdf";
import { loadLogoForPdf } from "../convocations/logoLoader";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

const fmtMoney = (n) => `EUR ${Number(n || 0).toFixed(2)}`;

/**
 * Genera un PDF "Lista logistica torneo" con:
 *  - intestazione (logo squadra + nome torneo + date + location + hotel)
 *  - tabella Camere (camera, capacità, atleti assegnati, note)
 *  - tabella Quote atleti (atleta, importo, stato, totali)
 *  - tabella Pasti genitori (genitore, figlio, pasti, importo, stato, totali)
 */
export async function downloadTournamentLogisticsPdf({ tournament, rooms, fees, meals, players, team }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;
  let y = margin;

  const playerMap = Object.fromEntries(players.map((p) => [p.id, p]));
  const playerLabel = (id) => {
    const p = playerMap[id];
    return p ? `${p.last_name} ${p.first_name}` : "—";
  };

  // ---- Header ----
  const logo = await loadLogoForPdf(team);
  if (logo) {
    try { doc.addImage(logo.dataUrl, logo.format, margin, y, 50, 50); } catch (err) { console.warn("Logistics PDF logo add failed:", err); }
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("LISTA LOGISTICA TORNEO", margin + (logo ? 60 : 0), y + 16);
  doc.setFontSize(13);
  doc.text(tournament.name || "", margin + (logo ? 60 : 0), y + 36);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const rightX = pageW - margin;
  doc.text(team?.name || "", rightX, y + 16, { align: "right" });
  doc.text(`Stagione ${tournament.season || team?.season || "—"}`, rightX, y + 30, { align: "right" });

  y += 70;

  // Tournament info block
  doc.setDrawColor(220);
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, y, pageW - margin * 2, 60, 6, 6, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text("DATE", margin + 12, y + 16);
  doc.text("LUOGO", margin + 150, y + 16);
  doc.text("HOTEL", margin + 290, y + 16);
  doc.text("ORGANIZZATORE", margin + 430, y + 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(20);
  const dateRange = formatDate(tournament.start_date) + (tournament.end_date ? `\n${formatDate(tournament.end_date)}` : "");
  doc.text(dateRange, margin + 12, y + 32);
  doc.text(tournament.location || "—", margin + 150, y + 32, { maxWidth: 130 });
  doc.text(tournament.hotel_name || "—", margin + 290, y + 32, { maxWidth: 130 });
  doc.text(tournament.organizer || "—", margin + 430, y + 32, { maxWidth: 120 });
  y += 80;

  const ensureSpace = (need) => {
    if (y + need > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const drawSectionTitle = (title) => {
    ensureSpace(30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(14, 165, 233);
    doc.text(title, margin, y);
    doc.setDrawColor(14, 165, 233);
    doc.setLineWidth(1.5);
    doc.line(margin, y + 4, margin + 60, y + 4);
    doc.setLineWidth(0.5);
    doc.setTextColor(20);
    y += 18;
  };

  const drawTableHeader = (cols) => {
    ensureSpace(24);
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y, pageW - margin * 2, 20, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    cols.forEach((c) => {
      doc.text(c.label.toUpperCase(), c.x, y + 13, { align: c.align || "left" });
    });
    doc.setTextColor(20);
    y += 22;
  };

  const drawRow = (cols, values, rowH = 18) => {
    ensureSpace(rowH);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    cols.forEach((c, i) => {
      const v = values[i] ?? "—";
      doc.text(String(v), c.x, y + 12, { align: c.align || "left", maxWidth: c.maxWidth });
    });
    doc.setDrawColor(230);
    doc.line(margin, y + rowH, pageW - margin, y + rowH);
    y += rowH;
  };

  // ---- Rooms ----
  drawSectionTitle("CAMERE HOTEL");
  if (rooms.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text("Nessuna camera assegnata.", margin, y + 10);
    doc.setTextColor(20);
    y += 24;
  } else {
    const cols = [
      { label: "Camera", x: margin + 6, maxWidth: 90 },
      { label: "Cap.", x: margin + 110, align: "center" },
      { label: "Atleti assegnati", x: margin + 150, maxWidth: 280 },
      { label: "Note", x: margin + 440, maxWidth: 100 },
    ];
    drawTableHeader(cols);
    rooms.forEach((r) => {
      const atleti = (r.player_ids || []).map(playerLabel).join(", ") || "—";
      const rowH = Math.max(18, Math.ceil(atleti.length / 60) * 12 + 6);
      drawRow(cols, [r.name, `${(r.player_ids || []).length}/${r.capacity}`, atleti, r.notes || ""], rowH);
    });
    const totAssigned = rooms.reduce((s, r) => s + (r.player_ids || []).length, 0);
    const totCap = rooms.reduce((s, r) => s + (r.capacity || 0), 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(`Totale: ${totAssigned}/${totCap} posti occupati su ${rooms.length} camere`, margin + 6, y + 14);
    y += 24;
  }

  // ---- Fees ----
  drawSectionTitle("QUOTE ATLETI");
  if (fees.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text("Nessuna quota registrata.", margin, y + 10);
    doc.setTextColor(20);
    y += 24;
  } else {
    const cols = [
      { label: "Atleta", x: margin + 6, maxWidth: 220 },
      { label: "Importo", x: margin + 280, align: "right" },
      { label: "Stato", x: margin + 360, align: "center" },
      { label: "Pagato il", x: margin + 460, align: "center" },
    ];
    drawTableHeader(cols);
    fees.forEach((f) => {
      drawRow(cols, [
        playerLabel(f.player_id),
        fmtMoney(f.amount),
        f.paid ? "PAGATO" : "DA PAGARE",
        f.paid_at ? formatDate(f.paid_at) : "—",
      ]);
    });
    const totDue = fees.reduce((s, f) => s + Number(f.amount || 0), 0);
    const totPaid = fees.filter((f) => f.paid).reduce((s, f) => s + Number(f.amount || 0), 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(
      `Totale dovuto: ${fmtMoney(totDue)} · Incassato: ${fmtMoney(totPaid)} · Residuo: ${fmtMoney(totDue - totPaid)}`,
      margin + 6,
      y + 14
    );
    y += 24;
  }

  // ---- Meals ----
  drawSectionTitle("PASTI GENITORI");
  if (meals.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text("Nessuna prenotazione registrata.", margin, y + 10);
    doc.setTextColor(20);
    y += 24;
  } else {
    const cols = [
      { label: "Genitore", x: margin + 6, maxWidth: 150 },
      { label: "Figlio", x: margin + 170, maxWidth: 140 },
      { label: "Pasti", x: margin + 330, align: "center" },
      { label: "Importo", x: margin + 400, align: "right" },
      { label: "Stato", x: margin + 480, align: "center" },
    ];
    drawTableHeader(cols);
    meals.forEach((m) => {
      drawRow(cols, [
        m.parent_name,
        m.player_id ? playerLabel(m.player_id) : "—",
        String(m.meals_count || 0),
        fmtMoney(m.amount),
        m.paid ? "PAGATO" : "DA PAGARE",
      ]);
    });
    const totMeals = meals.reduce((s, m) => s + Number(m.meals_count || 0), 0);
    const totDue = meals.reduce((s, m) => s + Number(m.amount || 0), 0);
    const totPaid = meals.filter((m) => m.paid).reduce((s, m) => s + Number(m.amount || 0), 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(
      `Totale: ${totMeals} pasti · Dovuto ${fmtMoney(totDue)} · Incassato ${fmtMoney(totPaid)} · Residuo ${fmtMoney(totDue - totPaid)}`,
      margin + 6,
      y + 14
    );
    y += 24;
  }

  // Footer
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `${team?.name || ""} · Lista logistica generata il ${new Date().toLocaleString("it-IT")}`,
      margin,
      pageH - 20
    );
    doc.text(`Pagina ${i} / ${totalPages}`, pageW - margin, pageH - 20, { align: "right" });
  }

  const safeName = (tournament.name || "torneo").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
  doc.save(`logistica_${safeName}.pdf`);
}
